import { z } from 'zod';

const traceSpanAttributesSchema = z.record(
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.union([z.string(), z.number(), z.boolean()])),
  ])
);

export type TraceSpanAttributes = z.infer<typeof traceSpanAttributesSchema>;

export type TraceSpan = {
  category: string;
  start_ns: number;
  end_ns: number;
  attributes: TraceSpanAttributes;
  children: TraceSpan[];
};

const traceSpanSchema: z.ZodType<TraceSpan> = z.lazy(() =>
  z.object({
    category: z.string(),
    start_ns: z.number(),
    end_ns: z.number(),
    attributes: traceSpanAttributesSchema,
    children: z.array(traceSpanSchema),
  })
);

const parseSpans = (spans: unknown[]): TraceSpan[] => {
  const parsed = z.array(traceSpanSchema).safeParse(spans);
  return parsed.success ? parsed.data : [];
};

export const PHASE_CATEGORIES = [
  'formula',
  'hook',
  'reasoning',
  'lookup',
  'rules',
] as const;

export type PhaseCategory = (typeof PHASE_CATEGORIES)[number];

export const isPhaseCategory = (value: string): value is PhaseCategory =>
  PHASE_CATEGORIES.some(category => category === value);

export type PhaseNode = {
  category: string;
  offsetMs: number;
  durMs: number;
  depth: number;
  attributes: TraceSpanAttributes;
};

export type PhaseTimeline = {
  nodes: PhaseNode[];
  wallMs: number;
};

const flatten = (
  siblings: TraceSpan[],
  depth = 0
): Array<{ span: TraceSpan; depth: number }> =>
  siblings.flatMap(span => [
    { span, depth },
    ...flatten(span.children ?? [], depth + 1),
  ]);

export const buildPhaseTimeline = (rawSpans: unknown[]): PhaseTimeline => {
  const flat = flatten(parseSpans(rawSpans));

  if (!flat.length) {
    return { nodes: [], wallMs: 0 };
  }

  const originNs = Math.min(...flat.map(({ span }) => span.start_ns));
  const endNs = Math.max(...flat.map(({ span }) => span.end_ns));
  const wallMs = (endNs - originNs) / 1e6;

  const nodes = flat.map(({ span, depth }) => ({
    category: span.category,
    offsetMs: (span.start_ns - originNs) / 1e6,
    durMs: (span.end_ns - span.start_ns) / 1e6,
    depth,
    attributes: span.attributes,
  }));

  return { nodes, wallMs };
};
